import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import type { Plan } from "../../../shared/goals/plan";
import { useNative } from "../../../shared/phone/media";
import { PnRow } from "../../../shared/phone/Row";
import { Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { Orb } from "../../components/ui/Orb";
import { runActive } from "../../lib/productsApi";
import { Spin } from "./Native";
import { PSUBS, STEP_ICONS, planSteps, type PlanStep } from "./planSteps";
import { RunLogSheet } from "./RunLog";
import { buildLog, runCounts, toolName } from "./runEvents";
import { useRun } from "./useRun";

interface Props {
  runId: string;
  /** In review the card waits behind "How it was made", with its steps folded until "Show the steps". */
  show: boolean;
  plan: Plan | null;
  approved: boolean;
  playbookTitle: string | null;
  /** The goal's playbook in the library, for "The playbook". */
  playbookItem: string | null;
  skillNames: string[];
  productName: string;
  profileConfirmed: boolean;
  /** Called once when a run this screen watched live comes to an end. */
  onEnded: () => void;
  onAgain: () => void;
  againBusy: boolean;
}

function HowBox({ step, onLog }: { step: PlanStep; onLog: () => void }) {
  return (
    <div className="howbox">
      {step.entries.map((e) =>
        e.k === "think" ? (
          <div className="hthink" key={e.key}>
            <Icon name="bulb" />
            <span>{e.x}</span>
          </div>
        ) : (
          <div className="trow" key={e.key}>
            <span className="tn">{e.k === "note" ? "Setup" : e.k === "err" && !e.tool ? "Error" : toolName(e.tool ?? "")}</span>
            <span className="tio">
              {e.x}
              {e.r && (
                <>
                  {" "}
                  <b>→ {e.r}</b>
                </>
              )}
            </span>
            <span className="tc" />
          </div>
        ),
      )}
      <div className="hf">
        <button type="button" className="chip link" onClick={onLog}>
          Open in the log
          <Icon name="right" />
        </button>
      </div>
    </div>
  );
}

function Step({ step, fresh, playbookItem, onLog }: { step: PlanStep; fresh: boolean; playbookItem: string | null; onLog: (keys: Set<string>) => void }) {
  const [how, setHow] = useState(false);
  return (
    <div className={`step${step.done ? " done" : ""}${fresh ? " in" : ""}`}>
      <span className="st-ic">{step.done ? <Icon name={STEP_ICONS[step.g]} /> : <span className="spin" />}</span>
      <div className={`st-b${fresh && step.done ? " fade" : ""}`}>
        <div className="st-t">{step.title}</div>
        {step.done ? (
          <>
            {step.res && <div className="st-r">{step.res}</div>}
            <div className="st-meta">
              {step.lessons?.map((id) => (
                <span className="lid" key={id}>
                  {id}
                </span>
              ))}
              {step.tag && <span className={`chip ${step.tag[0]}`}>{step.tag[1]}</span>}
              {step.g === 0 && step.res && playbookItem && (
                <Link className="chip link" to={`/library/${playbookItem}`}>
                  Read the playbook
                  <Icon name="right" />
                </Link>
              )}
              {step.entries.length > 0 && (
                <button type="button" className="chip link" onClick={() => setHow((h) => !h)}>
                  {how ? "Hide" : "How"}
                  <Icon name="right" />
                </button>
              )}
            </div>
            {how && <HowBox step={step} onLog={() => onLog(new Set(step.entries.map((e) => e.key)))} />}
          </>
        ) : (
          <div className="st-r dim">Working on it</div>
        )}
      </div>
      <span className="st-time">{step.t}</span>
    </div>
  );
}

/** The planning run, live: the prototype's run card with its five stages, steps, costs and full log. */
export function PlanRun(p: Props) {
  const native = useNative();
  const { run, events } = useRun(p.runId);
  const [open, setOpen] = useState(false);
  const [logHi, setLogHi] = useState<Set<string> | null>(null);
  const log = useMemo(() => buildLog(run, events), [run, events]);
  const active = !run || runActive(run.state);
  const steps = useMemo(
    () => planSteps(log, { running: active, plan: p.plan, playbookTitle: p.playbookTitle, skillNames: p.skillNames, approved: p.approved }),
    [log, active, p.plan, p.playbookTitle, p.skillNames, p.approved],
  );
  const counts = runCounts(run, log);

  // Why: only a run seen working here moves the page on; one already over when the page opened does not.
  const sawActive = useRef(false);
  const { onEnded } = p;
  useEffect(() => {
    if (!run) return;
    if (runActive(run.state)) sawActive.current = true;
    else if (sawActive.current) {
      sawActive.current = false;
      onEnded();
    }
  }, [run, onEnded]);

  const top = steps.length ? steps[steps.length - 1].g : 0;
  const failed = !!run && (run.state === "failed" || run.state === "cancelled");
  const pill = active ? ["live", "Planning"] : failed ? ["bad", "Stopped"] : ["plain", "Done"];
  const sub = !run
    ? "Starting"
    : run.state === "queued"
      ? "Starting"
      : active
        ? PSUBS[top]
        : failed
          ? run.error || "The run stopped before it saved a plan."
          : `Finished in ${counts.secs ?? 0} seconds. Nothing was spent on ads or sent to anyone.`;
  const sum = `${counts.calls} tool call${counts.calls === 1 ? "" : "s"} · ${counts.thoughts} thought${counts.thoughts === 1 ? "" : "s"}`;

  if (native) {
    if (failed)
      return (
        <div className="pn-card su-state">
          <h3>The plan was not written</h3>
          <p>{run?.error || "The run stopped before it saved a plan."}</p>
          <button type="button" className="pn-btn sm soft" disabled={p.againBusy} onClick={p.onAgain}>
            Plan again
          </button>
        </div>
      );
    if (active)
      return (
        <div className="pn-grp" aria-live="polite">
          {PSUBS.map((label, g) => {
            const s = steps.find((x) => x.g === g);
            // Why: a run can go past a stage it did not need; that stage is passed, not done.
            const skipped = !s && g < top;
            const done = !skipped && (g < top || !!s?.done);
            const on = !done && !skipped && g === top && !!run;
            return (
              <div className={`pn-row static${done || on ? "" : " su-later"}`} key={g}>
                <span className={`su-pst${done ? " ok" : ""}`}>{done ? <Icon name="check" /> : on ? <Spin /> : null}</span>
                <span className="m">
                  <span className="t su-wrap">{done && s ? s.title : label}</span>
                </span>
                {skipped ? <span className="v">Not needed</span> : s?.t && done ? <span className="v">{s.t}</span> : null}
              </div>
            );
          })}
        </div>
      );
    return (
      <>
        <div className="pn-grp">
          <PnRow icon={<Icon name="tool" />} iconTone="plain" title="How it was made" sub={counts.secs !== null ? `Written in ${counts.secs} seconds` : sum} onClick={() => setOpen(true)} chevron />
        </div>
        <Sheet
          open={open}
          onClose={() => setOpen(false)}
          tall
          title="How it was made"
          left={
            <button type="button" className="pn-tbtn strong" onClick={() => setOpen(false)}>
              Done
            </button>
          }
        >
          <p className="su-sheetsub">
            {sum}. Nothing was spent on ads or sent to anyone.
          </p>
          {steps.map((s) => (
            <section className="su-how" key={s.g}>
              <div className="pn-cap">{s.title}</div>
              <div className="pn-grp">
                {s.res && (
                  <div className="pn-row static su-full">
                    <span className="m">
                      <span className="t">{s.res}</span>
                    </span>
                  </div>
                )}
                {s.entries.map((e) => (
                  <div className={`pn-row static su-full su-ev ${e.k}`} key={e.key}>
                    <span className="m">
                      <span className="t">{e.x}</span>
                      {e.r ? <span className="s su-wrap">{e.r}</span> : null}
                    </span>
                    <span className="v">{e.t}</span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </Sheet>
      </>
    );
  }

  return (
    <section className={`now prun${open ? " open" : ""}${p.show ? " show" : ""}`} id="pnow">
      <div className="now-h">
        <div className="orb-tile">
          <Orb live={active} />
        </div>
        <div className="now-ht">
          <div className="now-t">Planning run</div>
          <div className="now-s">{sub}</div>
        </div>
        <span className={`pill ${pill[0]}`}>{pill[1]}</span>
      </div>
      {p.playbookTitle && (
        <div className="cnote prun-x" style={{ background: "var(--fire-soft)", color: "var(--fire-text)", marginTop: 14 }}>
          <Icon name="doc" />
          <span>
            Following <b>{p.playbookTitle}</b>
            {p.profileConfirmed ? `, the ${p.productName} profile and ` : " and "}
            {p.skillNames.length} skills.
          </span>
        </div>
      )}
      <div className="steps prun-x">
        {steps.map((s) => (
          <Step key={s.g} step={s} fresh={active} playbookItem={p.playbookItem} onLog={setLogHi} />
        ))}
      </div>
      {failed && (
        <div className="m-act prun-x">
          <button type="button" className="btn sm" disabled={p.againBusy} onClick={p.onAgain}>
            Plan again
          </button>
        </div>
      )}
      <div className="now-f">
        <span className="mono">{sum}</span>
        <span className="nf-a">
          <button type="button" className="replay prun-t" onClick={() => setOpen((o) => !o)}>
            <Icon name="chev" />
            <span className="o1">Show the steps</span>
            <span className="o2">Hide the steps</span>
          </button>
          {p.playbookItem && (
            <Link className="replay" to={`/library/${p.playbookItem}`}>
              <Icon name="doc" />
              The playbook
            </Link>
          )}
          <button type="button" className="replay" onClick={() => setLogHi(new Set())}>
            <Icon name="tool" />
            Full log
          </button>
        </span>
      </div>
      <RunLogSheet
        open={logHi !== null}
        onClose={() => setLogHi(null)}
        title="Planning log"
        sum={`${sum}${counts.secs !== null ? ` · ${counts.secs} seconds` : ""}`}
        log={log}
        hi={logHi ?? undefined}
      />
    </section>
  );
}
